/**
 * WithheldNarrativeNotice — the designed treatment for a narrative the
 * platform withheld (reviewer-voice quality gate, 2026-09-18).
 *
 * Replaces what used to ship as body prose: the internal ops sentinel
 * "… unavailable — content failed quality gate. Regenerate report.". The
 * section stays VISIBLE: a missing assessment (e.g. water rights) is a gating
 * gap the reader must see as open, not a section to drop silently.
 *
 * Variants:
 *   - `report`  — themed editorial callout inside a report section.
 *   - `dossier` — Findings dossier block (app chrome, not a themed report).
 *   - `inline`  — phrasing content for text walkers that render inside a <p>.
 *
 * REPORT PAGE (2026-10-13, William: no banners on report pages): the `report`
 * and `inline` variants render nothing where `useInSectionNoticesVisible()` is
 * false — the interactive report page. The delivered PDF / print tree (and
 * every host without that provider) still shows them, unchanged; on the page
 * the header status chip names the withheld section instead. The `dossier`
 * variant belongs to the Analyses tab, not a report page, and always renders.
 */
import React from 'react';
import type { ReportTheme } from '../../../types/reportTheme';
import { EditorialCallout } from './editorial/EditorialCallout';
import { useInSectionNoticesVisible } from './inSectionNotices';

export const WITHHELD_NARRATIVE_NOTICE_LABEL = 'Not assessed in this report';
export const WITHHELD_NARRATIVE_NOTICE_BODY =
  'Narrative withheld by an automated quality check; treat as open, not cleared.';

export type WithheldNarrativeNoticeProps =
  | { variant: 'report'; theme: ReportTheme; style?: React.CSSProperties }
  | { variant: 'dossier' }
  | { variant: 'inline' };

export const WithheldNarrativeNotice: React.FC<WithheldNarrativeNoticeProps> = (props) => {
  const visibleHere = useInSectionNoticesVisible();
  if (props.variant !== 'dossier' && !visibleHere) return null;
  if (props.variant === 'report') {
    return (
      <div data-testid="withheld-narrative-notice" data-withheld-narrative="true">
        <EditorialCallout
          theme={props.theme}
          tone="warning"
          label={WITHHELD_NARRATIVE_NOTICE_LABEL}
          style={props.style}
        >
          {WITHHELD_NARRATIVE_NOTICE_BODY}
        </EditorialCallout>
      </div>
    );
  }
  if (props.variant === 'dossier') {
    return (
      <div
        role="note"
        data-testid="withheld-narrative-notice"
        data-withheld-narrative="true"
        className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2.5 text-xs leading-relaxed text-text-secondary"
      >
        <p className="font-semibold text-text-primary">{WITHHELD_NARRATIVE_NOTICE_LABEL}</p>
        <p className="mt-0.5">{WITHHELD_NARRATIVE_NOTICE_BODY}</p>
      </div>
    );
  }
  return (
    <span
      data-testid="withheld-narrative-notice"
      data-withheld-narrative="true"
      style={{ fontStyle: 'italic' }}
    >
      <strong style={{ fontStyle: 'normal' }}>{WITHHELD_NARRATIVE_NOTICE_LABEL}</strong>
      {' – '}
      {WITHHELD_NARRATIVE_NOTICE_BODY.charAt(0).toLowerCase() + WITHHELD_NARRATIVE_NOTICE_BODY.slice(1)}
    </span>
  );
};
